// Офлайн-кэш аудио-резюме (LEARNING-SYSTEM-VISION §6.6).
//
// Аудио — единственный артефакт темы, НЕ покрытый PowerSync: конспект,
// глоссарий, карточки, тест и план голосовой сессии синкаются в SQLite, а
// аудио лежит бинарём на S3. Поэтому «офлайн» для него означает собственный
// файловый кэш поверх платформенного порта `audio-cache`.
//
// Три принципа, заложенные в этот модуль:
//   1. КЛЮЧ — ОТ recapId, НЕ ОТ URL. Presigned S3-ссылка живёт час и меняется
//      при каждом запросе; кэш по URL промахивался бы всегда.
//   2. КЭШ — BEST EFFORT. Браузер вправе выбросить Cache API (квота, приватный
//      режим, iOS 7-day eviction) в любой момент, поэтому ни один метод не
//      бросает, а бейдж «офлайн» рисуется строго по факту `isCached()`.
//   3. БЮДЖЕТ — LRU. `AUDIO_CACHE_BUDGET_BYTES` из `@studyhub/shared/learning`;
//      при переполнении вытесняем самые давние записи, а не самые большие.

import { audioCacheCapacitorAdapter } from "@studyhub/platform/adapters/capacitor/audio-cache.capacitor";
import { audioCacheWebAdapter } from "@studyhub/platform/adapters/web/audio-cache.web";
import type { AudioCachePort } from "@studyhub/platform/ports";
import { AUDIO_CACHE_BUDGET_BYTES } from "@studyhub/shared/learning";
import { getAccessToken } from "@/lib/auth-storage";
import { IS_CAPACITOR } from "@/lib/platform-detect";

// Выбор адаптера — прямой импорт + runtime-гейт (паттерн `lib/app-visible.ts`):
// conditional exports `platform-web`/`platform-capacitor` в проекте НЕ настроены,
// а `IS_CAPACITOR` — compile-time константа, поэтому неиспользуемая ветка
// вырезается tree-shaking'ом в соответствующей сборке.
const adapter: AudioCachePort = IS_CAPACITOR ? audioCacheCapacitorAdapter : audioCacheWebAdapter;

const BUDGET_BYTES = IS_CAPACITOR
	? AUDIO_CACHE_BUDGET_BYTES.capacitor
	: AUDIO_CACHE_BUDGET_BYTES.web;

/** Пространство ключей: `audio-recap:<uuid>` — стабильно и не пересекается с TTS. */
function cacheKey(recapId: string): string {
	return `audio-recap:${recapId}`;
}

// ── подписка на изменения кэша ──────────────────────────────────────────────
// Бейдж «✓ офлайн» обязан обновиться сразу после успешной загрузки, поэтому
// кэш публикует изменения; хук `useIsAudioCached` их слушает.

const listeners = new Set<() => void>();

function notifyChanged(): void {
	for (const listener of listeners) listener();
}

// ── сеть ────────────────────────────────────────────────────────────────────

const API_BASE = import.meta.env.VITE_API_URL ?? "";

/**
 * URL для СКАЧИВАНИЯ файла в кэш.
 *
 * Именно API-роут, а не presigned S3: бакет Beget не отдаёт
 * `Access-Control-Allow-Origin`, поэтому `fetch()` на S3-ссылку падает по CORS
 * и в Capacitor (origin `https://localhost`), и на web (кросс-поддомен
 * `files.study.iec.exchange`). Тег `<audio src>` CORS не требует — там
 * presigned ссылка работает, и мини-плеер продолжает её использовать.
 */
export function recapDownloadUrl(recapId: string): string {
	return `${API_BASE}/api/audio-recaps/${recapId}/stream`;
}

/** Bearer нужен только нашему API; на presigned S3 он ломает подпись запроса. */
function fetchInitFor(url: string): RequestInit {
	const isOwnApi = url.startsWith(`${API_BASE}/api/`) || url.startsWith("/api/");
	if (!isOwnApi) return { credentials: "omit" };
	const token = getAccessToken();
	return {
		credentials: "include",
		headers: token ? { Authorization: `Bearer ${token}` } : undefined,
	};
}

// Дедупликация параллельных загрузок одного recap'а (socket-событие + возврат
// в приложение + открытие страницы могут прийти почти одновременно).
const inFlight = new Map<string, Promise<boolean>>();

async function download(recapId: string, url: string): Promise<boolean> {
	try {
		const response = await fetch(url, fetchInitFor(url));
		if (!response.ok) return false;
		const blob = await response.blob();
		if (blob.size === 0) return false;
		await adapter.put(cacheKey(recapId), blob);
		await evictLru();
		notifyChanged();
		return true;
	} catch {
		// Нет сети / отказ CORS / переполнена квота — офлайн-копия опциональна.
		return false;
	}
}

// ── публичный API ───────────────────────────────────────────────────────────

export const audioOfflineCache = {
	/** Подписка на изменения кэша (положили/вытеснили/очистили). */
	subscribe(listener: () => void): () => void {
		listeners.add(listener);
		return () => {
			listeners.delete(listener);
		};
	},

	/** Лежит ли аудио в офлайн-кэше прямо сейчас. */
	async isCached(recapId: string): Promise<boolean> {
		return (await adapter.meta(cacheKey(recapId))) !== null;
	},

	/** Blob из кэша или `null`. Мини-плеер играет из него вместо presigned URL. */
	async getBlob(recapId: string): Promise<Blob | null> {
		return adapter.get(cacheKey(recapId));
	},

	/**
	 * Гарантировать наличие файла в кэше. Возвращает `true`, если файл лежит
	 * в кэше ПОСЛЕ вызова (уже был или скачался) — это же значение уходит в
	 * аналитику `audio_prefetched.cached`.
	 */
	async ensureCached(recapId: string, url = recapDownloadUrl(recapId)): Promise<boolean> {
		if (await audioOfflineCache.isCached(recapId)) return true;
		const existing = inFlight.get(recapId);
		if (existing) return existing;
		const task = download(recapId, url).finally(() => inFlight.delete(recapId));
		inFlight.set(recapId, task);
		return task;
	},

	/** Убрать одну запись (например, при удалении рекапа). */
	async evict(recapId: string): Promise<void> {
		await adapter.evict(cacheKey(recapId));
		notifyChanged();
	},

	/** Вытеснить самые давние записи, пока кэш не влезет в бюджет. */
	evictLru,

	/** Полная очистка (кнопка «Очистить» в настройках → Офлайн). */
	async clearAll(): Promise<void> {
		try {
			const keys = await adapter.keys();
			await Promise.all(keys.map((key) => adapter.evict(key)));
		} catch {
			// no-op: кэш best effort
		}
		notifyChanged();
	},

	/** Занятый объём в байтах — для строки «Занято N МБ». */
	async usageBytes(): Promise<number> {
		try {
			return await adapter.sizeBytes();
		} catch {
			return 0;
		}
	},

	/** Бюджет текущей платформы — чтобы UI мог показать «N из M МБ». */
	budgetBytes: BUDGET_BYTES,
};

async function evictLru(): Promise<void> {
	try {
		const keys = await adapter.keys();
		if (keys.length === 0) return;
		const entries = await Promise.all(
			keys.map(async (key) => ({ key, meta: await adapter.meta(key) })),
		);
		const known = entries
			.filter((entry): entry is { key: string; meta: { bytes: number; cachedAt: number } } =>
				Boolean(entry.meta),
			)
			.toSorted((a, b) => a.meta.cachedAt - b.meta.cachedAt);

		let total = known.reduce((sum, entry) => sum + entry.meta.bytes, 0);
		if (total <= BUDGET_BYTES) return;

		let evicted = false;
		for (const entry of known) {
			if (total <= BUDGET_BYTES) break;
			await adapter.evict(entry.key);
			total -= entry.meta.bytes;
			evicted = true;
		}
		if (evicted) notifyChanged();
	} catch {
		// Кэш недоступен — вытеснять нечего.
	}
}
